import { zodResolver } from "@hookform/resolvers/zod";
import { FC, useCallback, useEffect, useRef } from "react";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { z } from "@/i18n";
import { useFormStore } from "../form-store";
import { LinkedinFormPresent } from "../forms-presents/linkedin-form-present";
import { StandartFile } from "../common";
import { APISchemas, PATCH, POST } from "@/api";
import { Box, useToast } from "@chakra-ui/react";
import { handleError } from "./_handle-errors";
import isEqual from "lodash.isequal";
import { formUpdateMS } from "../contant";
import { useAuthStore } from "@/context/auth-store/auth-store";
import {
  checkVideoSize,
  checkGeneralImageFormat,
  checkContentMention,
  checkMPEGVideoFormat,
  checkVideoDuration,
  checkSingleMediaType,
  checkVideoAspectRatio,
} from "../../Post/platform-validations";
import { i18n } from "@/i18n";

export type linkedinSchema = z.infer<typeof linkedinSchema>;
// eslint-disable-next-line react-refresh/only-export-components
export const linkedinSchema = z
  .object({
    id: z.number().optional(),
    content: z
      .string()
      .max(3000, i18n.t("post-validations.linkedin.character-limit")),
    files: StandartFile.array().optional(),
  })
  .refine((o) => checkContentMention(o, 20), {
    path: ["root"],
    message: i18n.t("post-validations.linkedin.mentions-limit"),
  })
  .refine(checkMPEGVideoFormat, {
    path: ["root"],
    message: i18n.t("post-validations.linkedin.video-format"),
  })
  .refine(checkGeneralImageFormat, {
    path: ["root"],
    message: i18n.t("post-validations.linkedin.image-format"),
  })
  .refine((o) => checkVideoDuration(o, 600, 3), {
    path: ["root"],
    message: i18n.t("post-validations.twitter.video-duration"),
  })
  .refine((o) => checkVideoSize(o, 200 * 1024 * 1024), {
    path: ["root"],
    message: i18n.t("post-validations.linkedin.video-size"),
  })
  .refine((o) => checkVideoAspectRatio(o, 1 / 2.4, 2.4), {
    path: ["root"],
    message: i18n.t("post-validations.linkedin.video-aspectRatio"),
  })
  .refine(checkSingleMediaType, {
    path: ["root"],
    message: i18n.t("post-validations.linkedin.single-media-type"),
  });

export const LinkedinFormContainer: FC<{
  channel: APISchemas["Channel"] & { hasError?: boolean };
  defaultValue?: linkedinSchema;
}> = ({ channel, defaultValue }) => {
  const defaultValueRef = useRef<typeof defaultValue>();
  const [formState, setFormState] = useFormStore((f) => [
    f.fromStates[channel.id.toString()] ?? {
      sync: true,
    },
    f.setFormState,
  ]);

  const toast = useToast();
  const activeOrganization = useAuthStore((f) => f.activeOrganization);

  const formSync = useFormStore((f) => f.formSync);

  const {
    main,
    scheduledAt,
    addLinkedinPost,
    setForm,
    subscribe,
    unsubscribe,
    blockProgress,
    goToStep,
    setPlatformHasError,
    setGlobalError,
  } = useFormStore((s) => ({
    main: s.mainForms.main,
    setForm: s.setForm,
    subscribe: s.subscribe,
    unsubscribe: s.unsubscribe,
    addLinkedinPost: s.addLinkedinPost,
    scheduledAt: s.scheduledAt,
    blockProgress: s.setProgressBlock,
    setPlatformHasError: s.setPlatformHasError,
    goToStep: s.goToStep,
    setGlobalError: s.setGlobalError,
  }));

  const methods = useForm<linkedinSchema>({
    resolver: zodResolver(linkedinSchema),
  });
  const {
    getValues,
    trigger,
    handleSubmit,
    reset,
    formState: { isDirty },
  } = methods;

  if (!isEqual(defaultValue, defaultValueRef.current)) {
    reset(defaultValue);
    setFormState(channel.id.toString(), {
      sync: false,
    });
    defaultValueRef.current = defaultValue;
  }

  const draftForm = useCallback(async () => {
    const result = await trigger();

    if (!result) {
      setPlatformHasError(channel, true);
      return;
    }

    setPlatformHasError(channel, false);
    setForm(channel.id, getValues());
  }, [channel, getValues, setForm, setPlatformHasError, trigger]);

  const onSubmit: (
    status: APISchemas["StatusCf9Enum"]
  ) => SubmitHandler<linkedinSchema> = useCallback(
    (status) => async (data) => {
      if (!activeOrganization) {
        return;
      }
      const coverUrls: Record<string, string> = {};
      data.files?.forEach((file) => {
        if (!file.data.video) {
          return;
        }
        if (!("coverUrl" in file.data.video)) {
          return;
        }
        if (
          !file.data.video.coverUrl ||
          typeof file.data.video.coverUrl !== "string"
        ) {
          return;
        }
        coverUrls[file.data.video.id] = file.data.video.coverUrl;
      });
      const body: APISchemas["LinkedinPostCreateRequest"] = {
        body: data.content,
        channel: channel.id,
        status,
        metadata: {
          cover_url: coverUrls,
        },
        scheduled_at: scheduledAt.toISOString(),
        media: data.files
          ?.map((f) => f.data.image?.id || f.data.video?.id)
          .filter((m): m is string => !!m)
          .map((m) => Number(m)),
      };
      const { data: post, error } = defaultValueRef.current?.id
        ? await PATCH("/linkedin/{org_pk}/post/{id}/", {
            params: {
              path: {
                org_pk: activeOrganization.id.toString(),
                id: defaultValueRef.current.id,
              },
            },
            body,
          })
        : await POST("/linkedin/{org_pk}/post/", {
            params: {
              path: {
                org_pk: activeOrganization.id.toString(),
              },
            },
            body,
          });

      if (error) {
        blockProgress(true);
        handleError(channel.id.toString(), error, setGlobalError);
        setPlatformHasError(channel, true);
        goToStep?.(1);
        return;
      }

      if (!post) {
        toast({
          status: "error",
          title: i18n.t("errors.unexpected-error-contact-customer-service"),
        });
        return;
      }
      setPlatformHasError(channel, false);
      addLinkedinPost(post);
    },
    [
      activeOrganization,
      addLinkedinPost,
      blockProgress,
      channel,
      goToStep,
      scheduledAt,
      setGlobalError,
      setPlatformHasError,
      toast,
    ]
  );

  useEffect(() => {
    if (main && formState.sync) {
      reset({
        content: main.content,
        files: main.files,
      });
    }
  }, [formState.sync, main, reset]);

  useEffect(() => {
    if (isDirty) {
      setFormState(channel.id.toString(), {
        sync: false,
      });
    }
  }, [channel.id, isDirty, setFormState]);

  useEffect(() => {
    const submitFn = handleSubmit(onSubmit("planned"));
    const draftFn = handleSubmit(onSubmit("draft"));
    subscribe("submit", submitFn);
    subscribe("check", draftForm);
    subscribe("draft", draftFn);

    return () => {
      unsubscribe("submit", submitFn);
      unsubscribe("check", draftForm);
      unsubscribe("draft", draftFn);
    };
  }, [draftForm, handleSubmit, onSubmit, subscribe, unsubscribe]);

  useEffect(() => {
    if (!formSync) {
      return;
    }

    let prev: linkedinSchema = {};

    const interval = setInterval(() => {
      const values = getValues();

      if (isEqual(prev, values)) {
        return;
      }

      if (channel.hasError) {
        draftForm();
      }

      setForm(channel.id, values);
      prev = values;
    }, formUpdateMS);

    return () => {
      clearInterval(interval);
    };
  }, [channel, draftForm, formSync, getValues, setForm]);

  return (
    <Box border="1px solid #E2E4E6" rounded="md" px="3" pos="relative">
      <form>
        <FormProvider {...methods}>
          <LinkedinFormPresent channel={channel} />
        </FormProvider>
      </form>
    </Box>
  );
};
